import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, FileText, Calendar, CheckCircle2, XCircle, Clock, Pencil, Upload, Save, Bookmark, X, CalendarClock } from "lucide-react";
import SEO from "../components/common/SEO";
import { Reveal } from "../components/common/Reveal";
import PillButton from "../components/common/PillButton";
import { portalMe, portalApplications, updateProfile, uploadPortalResume, getSaved, removeSaved, withdrawApplication, bookInterview, requestReschedule, getToken, clearToken, API } from "../lib/api";
import { useContent } from "../context/ContentContext";
import { fmtInTz, localTimeShort, differsFromLocal } from "../lib/timezones";
import { WORK_AUTH_OPTIONS } from "../lib/workAuth";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const PROGRESSION = ["Submitted", "Under Review", "Shortlisted", "Interviewing", "Offer", "Hired"];

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

function StatusBadge({ status }) {
  const styles =
    status === "Hired"
      ? "bg-emerald-400/15 text-emerald-300 border-emerald-400/30"
      : status === "Not Selected"
      ? "bg-red-400/15 text-red-300 border-red-400/30"
      : "bg-sky-400/15 text-sky-300 border-sky-400/30";
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${styles}`} data-testid="app-status-badge">
      {status === "Hired" ? <CheckCircle2 className="h-3.5 w-3.5" /> : status === "Not Selected" ? <XCircle className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}
      {status}
    </span>
  );
}

function Stepper({ status }) {
  if (status === "Not Selected" || status === "Withdrawn") {
    const msg = status === "Withdrawn"
      ? "You withdrew this application. You're welcome to apply for other opportunities anytime."
      : "This application was not selected to move forward. We encourage you to apply for other opportunities.";
    return (
      <div className="mt-6 rounded-xl border border-slate-400/20 bg-slate-400/5 px-5 py-4 text-sm text-slate-300" data-testid="stepper-terminal">
        {msg}
      </div>
    );
  }
  const currentIdx = Math.max(0, PROGRESSION.indexOf(status));
  return (
    <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
      {PROGRESSION.map((stage, i) => {
        const done = i <= currentIdx;
        return (
          <div key={stage} className="flex flex-col gap-2">
            <div className={`h-1 rounded-full ${done ? "bg-sky-400" : "bg-white/10"}`} />
            <span className={`text-xs font-medium ${done ? "text-white" : "text-slate-500"}`}>{stage}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function PortalDashboard() {
  const navigate = useNavigate();
  const { getJob } = useContent();
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [apps, setApps] = useState([]);
  const [editing, setEditing] = useState(false);
  const [profile, setProfile] = useState({ name: "", phone: "", linkedin: "", primary_skill: "", work_authorization: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [savedSlugs, setSavedSlugs] = useState([]);
  const [withdrawId, setWithdrawId] = useState(null);
  const [reschedulingId, setReschedulingId] = useState(null);
  const TERMINAL = ["Hired", "Not Selected", "Withdrawn"];

  const confirmWithdraw = async () => {
    const id = withdrawId;
    setWithdrawId(null);
    try {
      await withdrawApplication(id);
      setApps((list) => list.map((a) => (a.id === id
        ? { ...a, status: "Withdrawn", status_history: [...(a.status_history || []), { status: "Withdrawn", at: new Date().toISOString(), note: "Withdrawn by candidate" }] }
        : a)));
      toast.success("Application withdrawn", { description: "Recruiters have been notified." });
    } catch {
      toast.error("Could not withdraw application");
    }
  };

  const setP = (e) => setProfile((p) => ({ ...p, [e.target.name]: e.target.value }));

  const chooseSlot = async (appId, slot, tz) => {
    try {
      const res = await bookInterview(appId, slot);
      setApps((list) => list.map((a) => (a.id === appId ? { ...a, interview_booked: slot } : a)));
      setReschedulingId(null);
      toast.success(res.rescheduled ? "Interview rescheduled" : "Interview booked", { description: fmtInTz(slot, tz) });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not book that time");
    }
  };

  const askReschedule = async (appId) => {
    const message = window.prompt("Optionally tell the recruiter what times work better for you:", "") ?? "";
    try {
      await requestReschedule(appId, message);
      setApps((list) => list.map((a) => (a.id === appId ? { ...a, reschedule_requested: true } : a)));
      toast.success("Request sent", { description: "The recruiter will reach out with new times." });
    } catch {
      toast.error("Could not send your request");
    }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const updated = await updateProfile(profile);
      setMe(updated);
      setEditing(false);
      toast.success("Profile updated");
    } catch {
      toast.error("Could not update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const unsaveJob = async (slug) => {
    try {
      const res = await removeSaved(slug);
      setSavedSlugs(res.saved || []);
      toast.success("Removed from saved jobs");
    } catch {
      toast.error("Could not update saved jobs");
    }
  };

  const onResumeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("resume", file);
      const res = await uploadPortalResume(fd);
      setMe((m) => ({ ...m, resume_name: res.resume_name }));
      toast.success("Resume updated", { description: res.resume_name });
    } catch {
      toast.error("Upload failed, please try again");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  useEffect(() => {
    if (!getToken()) {
      navigate("/portal/login");
      return;
    }
    Promise.all([portalMe(), portalApplications(), getSaved().catch(() => ({ saved: [] }))])
      .then(([meData, appData, savedData]) => {
        setMe(meData);
        setProfile({
          name: meData.name || "",
          phone: meData.phone || "",
          linkedin: meData.linkedin || "",
          primary_skill: meData.primary_skill || "",
          work_authorization: meData.work_authorization || "",
        });
        setApps(appData.applications || []);
        setSavedSlugs(savedData.saved || []);
      })
      .catch(() => {
        clearToken();
        navigate("/portal/login");
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const logout = () => {
    clearToken();
    navigate("/portal/login");
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-sky-400" />
      </div>
    );
  }

  return (
    <>
      <SEO title="My Applications" path="/portal" />
      <section className="px-6 pb-24 pt-36 lg:pt-44">
        <div className="mx-auto max-w-5xl">
          <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
            <div>
              <div className="mb-3 text-xs font-medium uppercase tracking-[0.25em] text-sky-400">Candidate Portal</div>
              <h1 className="font-heading text-4xl font-bold tracking-tighter text-white lg:text-5xl">My Applications</h1>
              {me && <p className="mt-3 text-slate-400" data-testid="portal-user-email">Signed in as {me.email}</p>}
            </div>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 self-start rounded-full border border-white/15 px-5 py-2.5 text-sm text-white transition-colors hover:border-sky-400/60 hover:text-sky-400"
              data-testid="portal-logout"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>

          {/* Profile + Resume */}
          <div className="mt-10 grid gap-6 lg:grid-cols-2">
            <Reveal>
              <div className="h-full rounded-2xl border border-white/10 bg-ink-800/50 p-8" data-testid="portal-profile">
                <div className="flex items-center justify-between">
                  <h2 className="font-heading text-xl font-bold tracking-tight text-white">My Profile</h2>
                  {!editing ? (
                    <button onClick={() => setEditing(true)} className="inline-flex items-center gap-1.5 text-sm font-medium text-sky-400 hover:text-sky-300" data-testid="profile-edit">
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </button>
                  ) : null}
                </div>
                {!editing ? (
                  <dl className="mt-5 space-y-3 text-sm">
                    {[["Name", me?.name], ["Phone", me?.phone], ["LinkedIn", me?.linkedin], ["Primary Skill", me?.primary_skill], ["Work Authorization", me?.work_authorization]].map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-4 border-b border-white/5 pb-3">
                        <dt className="text-slate-500">{k}</dt>
                        <dd className="text-right text-slate-200">{v || <span className="text-slate-600">Not set</span>}</dd>
                      </div>
                    ))}
                  </dl>
                ) : (
                  <div className="mt-5 space-y-4">
                    {[["name", "Name"], ["phone", "Phone"], ["linkedin", "LinkedIn"], ["primary_skill", "Primary Skill"]].map(([name, label]) => (
                      <div key={name}>
                        <label className="mb-1.5 block text-xs font-medium uppercase tracking-[0.15em] text-slate-400">{label}</label>
                        <input
                          name={name}
                          value={profile[name]}
                          onChange={setP}
                          className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm text-white outline-none focus:border-sky-400/70"
                          data-testid={`profile-input-${name}`}
                        />
                      </div>
                    ))}
                    <div>
                      <label className="mb-1.5 block text-xs font-medium uppercase tracking-[0.15em] text-slate-400">Work Authorization / Visa</label>
                      <select
                        name="work_authorization"
                        value={profile.work_authorization}
                        onChange={setP}
                        className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm text-white outline-none focus:border-sky-400/70"
                        data-testid="profile-input-work_authorization"
                      >
                        <option value="" className="bg-ink-800">Not set</option>
                        {WORK_AUTH_OPTIONS.map((o) => (
                          <option key={o} value={o} className="bg-ink-800">{o}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex gap-3 pt-1">
                      <button onClick={saveProfile} disabled={savingProfile} className="inline-flex items-center gap-2 rounded-full bg-sky-400 px-5 py-2.5 text-sm font-medium text-ink-900 hover:bg-sky-300 disabled:opacity-60" data-testid="profile-save">
                        <Save className="h-4 w-4" /> {savingProfile ? "Saving..." : "Save"}
                      </button>
                      <button onClick={() => setEditing(false)} className="rounded-full border border-white/15 px-5 py-2.5 text-sm text-white hover:border-white/30">Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="flex h-full flex-col rounded-2xl border border-white/10 bg-ink-800/50 p-8" data-testid="portal-resume">
                <h2 className="font-heading text-xl font-bold tracking-tight text-white">My Resume</h2>
                <div className="mt-5 flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                  <FileText className="h-5 w-5 text-sky-400" />
                  <span className="text-sm text-slate-300" data-testid="portal-resume-name">
                    {me?.resume_name || "No resume on file"}
                  </span>
                </div>
                <p className="mt-4 text-sm text-slate-400">Upload a new version anytime. Your latest resume is shared with recruiters.</p>
                <label className="mt-5 inline-flex w-fit cursor-pointer items-center gap-2 rounded-full bg-sky-400 px-5 py-2.5 text-sm font-medium text-ink-900 transition-colors hover:bg-sky-300" data-testid="portal-resume-upload-label">
                  <Upload className="h-4 w-4" /> {uploading ? "Uploading..." : "Upload New Resume"}
                  <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={onResumeUpload} disabled={uploading} data-testid="portal-resume-upload" />
                </label>
              </div>
            </Reveal>
          </div>

          <h2 className="mt-16 font-heading text-2xl font-bold tracking-tight text-white">Saved Jobs</h2>
          {savedSlugs.length === 0 ? (
            <div className="mt-6 rounded-2xl border border-white/10 bg-ink-800/40 p-8 text-sm text-slate-400" data-testid="portal-saved-empty">
              You haven&apos;t saved any jobs yet. Browse <Link to="/careers" className="text-sky-400 hover:underline">open positions</Link> and tap Save to bookmark them.
            </div>
          ) : (
            <div className="mt-6 grid gap-4 md:grid-cols-2" data-testid="portal-saved-jobs">
              {savedSlugs.map((slug) => {
                const job = getJob(slug);
                if (!job) return null;
                return (
                  <div key={slug} className="flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-ink-800/40 p-6" data-testid={`saved-job-${slug}`}>
                    <div>
                      <div className="mb-1.5 inline-flex items-center gap-1.5 text-[11px] text-sky-400">
                        <Bookmark className="h-3 w-3" /> {job.type}
                      </div>
                      <h3 className="font-heading text-lg font-bold tracking-tight text-white">{job.role}</h3>
                      <p className="mt-1 text-sm text-slate-400">{job.location}</p>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-2">
                      <Link to={`/careers/${slug}`} className="rounded-full border border-white/15 px-4 py-2 text-sm text-white transition-colors hover:border-sky-400/60 hover:text-sky-400">
                        View & Apply
                      </Link>
                      <button onClick={() => unsaveJob(slug)} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-300" data-testid={`unsave-${slug}`}>
                        <X className="h-3 w-3" /> Remove
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <h2 className="mt-16 font-heading text-2xl font-bold tracking-tight text-white">Applications</h2>
          {apps.length === 0 ? (
            <div className="mt-12 rounded-2xl border border-white/10 bg-ink-800/40 p-12 text-center" data-testid="portal-empty">
              <p className="text-slate-400">You don&apos;t have any applications yet.</p>
              <div className="mt-6 flex justify-center">
                <PillButton to="/careers">Explore Careers</PillButton>
              </div>
            </div>
          ) : (
            <div className="mt-12 space-y-6" data-testid="portal-applications">
              {apps.map((app) => (
                <Reveal key={app.id}>
                  <div className="rounded-2xl border border-white/10 bg-ink-800/50 p-8" data-testid={`portal-app-${app.id}`}>
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <h2 className="font-heading text-xl font-bold tracking-tight text-white">
                          {app.role || "General Application"}
                        </h2>
                        <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-slate-400">
                          <span className="inline-flex items-center gap-1.5">
                            <Calendar className="h-3.5 w-3.5" /> Submitted {fmtDate(app.created_at)}
                          </span>
                          {app.primary_skill && (
                            <span className="inline-flex items-center gap-1.5">
                              <FileText className="h-3.5 w-3.5" /> {app.primary_skill}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-start gap-2 sm:items-end">
                        <StatusBadge status={app.status} />
                        {!TERMINAL.includes(app.status) && (
                          <button
                            onClick={() => setWithdrawId(app.id)}
                            className="text-xs font-medium text-slate-500 underline-offset-2 hover:text-red-300 hover:underline"
                            data-testid={`withdraw-${app.id}`}
                          >
                            Withdraw application
                          </button>
                        )}
                      </div>
                    </div>

                    <Stepper status={app.status} />

                    {(app.interview_booked || (app.interview_slots && app.interview_slots.length > 0)) && app.status !== "Withdrawn" && (
                      <div className="mt-6 rounded-xl border border-sky-400/25 bg-sky-400/5 p-5" data-testid={`interview-panel-${app.id}`}>
                        <div className="flex items-center gap-2 text-sm font-semibold text-sky-300">
                          <CalendarClock className="h-4 w-4" /> Interview Scheduling
                        </div>
                        {app.interview_booked && reschedulingId !== app.id ? (
                          <div className="mt-3 space-y-3" data-testid={`interview-confirmed-${app.id}`}>
                            <div className="text-sm text-white">
                              Confirmed for <strong>{fmtInTz(app.interview_booked, app.interview_timezone)}</strong>
                              {differsFromLocal(app.interview_booked, app.interview_timezone) ? <span className="text-slate-400"> · {localTimeShort(app.interview_booked)} your time</span> : null}
                              {app.interview_location ? <span className="text-slate-400"> · {app.interview_location}</span> : null}
                            </div>
                            <div className="flex flex-wrap items-center gap-3">
                              <a
                                href={`${API}/interview/${app.id}.ics`}
                                className="inline-flex items-center gap-1.5 rounded-full border border-sky-400/40 px-4 py-1.5 text-xs font-medium text-sky-300 transition-colors hover:bg-sky-400/10"
                                data-testid={`interview-ics-${app.id}`}
                              >
                                Add to calendar
                              </a>
                              {(app.interview_slots && app.interview_slots.length > 1) && (
                                <button
                                  onClick={() => setReschedulingId(app.id)}
                                  className="text-xs font-medium text-slate-400 underline-offset-2 hover:text-sky-300 hover:underline"
                                  data-testid={`interview-change-${app.id}`}
                                >
                                  Change time
                                </button>
                              )}
                              {app.reschedule_requested ? (
                                <span className="text-xs font-medium text-amber-300" data-testid={`interview-reschedule-pending-${app.id}`}>
                                  Reschedule requested — we&apos;ll be in touch
                                </span>
                              ) : (
                                <button
                                  onClick={() => askReschedule(app.id)}
                                  className="text-xs font-medium text-slate-400 underline-offset-2 hover:text-sky-300 hover:underline"
                                  data-testid={`interview-request-${app.id}`}
                                >
                                  Request a different time
                                </button>
                              )}
                            </div>
                          </div>
                        ) : (
                          <>
                            <p className="mt-2 text-sm text-slate-400">
                              {reschedulingId === app.id ? "Pick a new time" : "Pick a time that works for you"}{app.interview_location ? ` · ${app.interview_location}` : ""}.
                            </p>
                            <div className="mt-4 flex flex-wrap gap-2">
                              {app.interview_slots.map((slot) => {
                                const isCurrent = slot === app.interview_booked;
                                return (
                                  <button
                                    key={slot}
                                    onClick={() => chooseSlot(app.id, slot, app.interview_timezone)}
                                    className={`rounded-full border px-4 py-2 text-sm transition-colors ${isCurrent ? "border-sky-400/60 bg-sky-400/15 text-sky-200" : "border-white/15 bg-white/[0.03] text-white hover:border-sky-400/60 hover:bg-sky-400/10 hover:text-sky-200"}`}
                                    data-testid={`interview-slot-${app.id}`}
                                  >
                                    {fmtInTz(slot, app.interview_timezone)}
                                    {differsFromLocal(slot, app.interview_timezone) ? ` (${localTimeShort(slot)} your time)` : ""}
                                    {isCurrent ? " (current)" : ""}
                                  </button>
                                );
                              })}
                            </div>
                            {reschedulingId === app.id && (
                              <button onClick={() => setReschedulingId(null)} className="mt-3 text-xs text-slate-500 hover:text-slate-300" data-testid={`interview-cancel-reschedule-${app.id}`}>
                                Keep current time
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {app.status_history && app.status_history.length > 0 && (
                      <div className="mt-8 border-t border-white/5 pt-6">
                        <div className="mb-4 text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Status History</div>
                        <ul className="space-y-4">
                          {[...app.status_history].reverse().map((h, i) => (
                            <li key={i} className="flex items-start gap-3">
                              <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-sky-400" />
                              <div>
                                <div className="text-sm font-medium text-white">{h.status}</div>
                                <div className="text-xs text-slate-500">{fmtDate(h.at)}</div>
                                {h.note && <p className="mt-1 text-sm text-slate-400">{h.note}</p>}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <AlertDialog open={!!withdrawId} onOpenChange={(o) => !o && setWithdrawId(null)}>
        <AlertDialogContent className="border-white/10 bg-ink-800 text-white" data-testid="withdraw-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Withdraw this application?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              This will remove your application from active consideration. You can always apply again later. This action can&apos;t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/15 bg-transparent text-white hover:bg-white/5">Keep application</AlertDialogCancel>
            <AlertDialogAction onClick={confirmWithdraw} className="bg-red-500 text-white hover:bg-red-400" data-testid="withdraw-confirm">
              Yes, withdraw
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
